/**
 * AreaFindingSheet — bottom sheet for general area finding (not bound to a device).
 * Props:
 *   open: boolean
 *   floorPlanId: string
 *   area: { area_id, name }
 *   serviceId: string
 *   allFindings: AreaFinding[]  // ALL findings for the plan (history)
 *   onClose: fn
 *   onSaved(finding): fn
 */
import React, { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, AlertTriangle, History } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RISK_OPTS = [
    { v: "bajo",  l: "Bajo (OK)" },
    { v: "medio", l: "Medio (Atención)" },
    { v: "alto",  l: "Alto (Alerta)" },
];

const PEST_OPTS = [
    { v: "raton",               l: "Ratón" },
    { v: "rata",                l: "Rata" },
    { v: "insectos_rastreros",  l: "Insectos rastreros" },
    { v: "insectos_voladores",  l: "Insectos voladores" },
];

const RISK_LBL = Object.fromEntries(RISK_OPTS.map((o) => [o.v, o.l]));
const PEST_LBL = Object.fromEntries(PEST_OPTS.map((o) => [o.v, o.l]));

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("es-SV", { year: "numeric", month: "short", day: "numeric" });
    } catch { return iso?.slice(0, 10) || ""; }
};

export default function AreaFindingSheet({ open, area, floorPlanId, serviceId, allFindings = [], onClose, onSaved }) {
    const [form, setForm] = useState({ risk_level: "bajo", pest_type: "raton", notes: "" });
    const [busy, setBusy] = useState(false);

    // History for THIS area sorted desc by created_at
    const history = useMemo(() => {
        if (!area) return [];
        return allFindings
            .filter((f) => f.area_id === area.area_id)
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }, [area, allFindings]);

    // Existing finding for the CURRENT service (used as base for upsert / editing)
    const currentServiceFinding = useMemo(
        () => history.find((f) => f.service_id === serviceId) || null,
        [history, serviceId]
    );
    // Most recent finding across services — used to prefill if this is a NEW service
    const latestFinding = history[0] || null;

    React.useEffect(() => {
        if (!area) return;
        if (currentServiceFinding) {
            // Editing today's finding — exact prefill
            setForm({
                risk_level: currentServiceFinding.risk_level || "bajo",
                pest_type: currentServiceFinding.pest_type || "raton",
                notes: currentServiceFinding.notes || "",
            });
        } else if (latestFinding) {
            // New service but past memory exists — prefill from latest (tech can adjust)
            setForm({
                risk_level: latestFinding.risk_level || "bajo",
                pest_type: latestFinding.pest_type || "raton",
                notes: "",
            });
        } else {
            setForm({ risk_level: "bajo", pest_type: "raton", notes: "" });
        }
    }, [area?.area_id, currentServiceFinding, latestFinding]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!area) return null;

    const handleSave = async () => {
        if (!serviceId) {
            toast.error("Falta el ID del servicio");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                service_id: serviceId,
                floor_plan_id: floorPlanId,
                area_id: area.area_id,
                risk_level: form.risk_level,
                pest_type: form.pest_type,
                notes: form.notes,
            };
            const r = await api.post("/area-findings", payload);
            toast.success(currentServiceFinding ? "Hallazgo actualizado" : "Hallazgo registrado");
            onSaved?.(r.data);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally {
            setBusy(false);
        }
    };

    const pastHistory = history.filter((f) => f.service_id !== serviceId);

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto" data-testid="area-finding-sheet">
                <SheetHeader className="text-left">
                    <SheetTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-blue-600" />
                        Hallazgo de área
                    </SheetTitle>
                    <SheetDescription className="text-sm">
                        {area.name}
                        {currentServiceFinding && (
                            <span className="ml-2 inline-block bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded font-semibold align-middle" data-testid="area-editing-badge">
                                Editando hallazgo de este servicio
                            </span>
                        )}
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-4 mt-4 pb-4">
                    <div>
                        <Label>Nivel de riesgo</Label>
                        <Select value={form.risk_level} onValueChange={(v) => setForm({ ...form, risk_level: v })}>
                            <SelectTrigger data-testid="area-risk-level"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {RISK_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Medio = marcador naranja · Alto = marcador rojo · Bajo = sin alerta
                        </p>
                    </div>
                    <div>
                        <Label>Tipo de plaga</Label>
                        <Select value={form.pest_type} onValueChange={(v) => setForm({ ...form, pest_type: v })}>
                            <SelectTrigger data-testid="area-pest-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PEST_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Notas</Label>
                        <Textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Observaciones..."
                            data-testid="area-notes"
                        />
                    </div>

                    {/* History (internal — never visible to clients) */}
                    {pastHistory.length > 0 && (
                        <div className="border-t pt-3" data-testid="area-history">
                            <div className="flex items-center gap-1.5 mb-2">
                                <History className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-xs font-semibold text-slate-600">
                                    Historial ({pastHistory.length}) — solo interno
                                </span>
                            </div>
                            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                                {pastHistory.slice(0, 10).map((h) => (
                                    <li
                                        key={h.id}
                                        className="text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5"
                                        data-testid={`area-history-item-${h.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-slate-500">{fmtDate(h.created_at)}</span>
                                            <span
                                                className={`px-1.5 py-0.5 rounded text-white text-[9px] font-semibold ${
                                                    h.risk_level === "alto" ? "bg-red-600" :
                                                    h.risk_level === "medio" ? "bg-orange-500" :
                                                    "bg-emerald-600"
                                                }`}
                                            >
                                                {RISK_LBL[h.risk_level] || h.risk_level}
                                            </span>
                                        </div>
                                        <div className="text-slate-700 mt-0.5">
                                            {PEST_LBL[h.pest_type] || h.pest_type}
                                            {h.notes ? ` — ${h.notes}` : ""}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <SheetFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 pb-4 flex gap-2">
                    <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                    <Button onClick={handleSave} disabled={busy} className="flex-1" data-testid="area-save">
                        <Save className="w-4 h-4 mr-1" /> {currentServiceFinding ? "Actualizar" : "Guardar"}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
